import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { TextInput, Text, Button, colors } from 'react-orange-ui';
import styled from 'styled-components';

import WordingService from '../../../services/WordingService';
import TagService from '../../../services/TagService';
import ApiService from '../../../services/ApiService';
import Spinner from '../../Spinner/Spinner';

const MOBILE_TYPE = 'M';
const EMAIL_TYPE = 'E';

const Helper = styled.div`
  display: ${props => props.display ? 'block' : 'none'};
  width: 100%;
  background-color: ${colors.grey6};
  padding: 1rem;
  margin-bottom: 1rem;
`;

class ContactView extends Component {
  state = {
    contactMobile: '',
    contactMail: '',
    messageHelp: null,
    isFetching: true,
    errorMobile: null,
    errorMail: null,
    buttonLabel: WordingService.getWording('button', 'validate'),
  };

  componentDidMount() {
    this.getContactsData();
  }

  /**
   * Get all contacts data
   */
  getContactsData = async () => {
    const { identitiesId, goToErrorPage } = this.props;

    try {
      const res = await ApiService.getIdentitiesContacts({ identitiesId });
      const contacts = res.data.data;

      for (const contact of contacts) {
        const { contactData } = await this.getContactData(contact);
        
        if (contact.contactType === MOBILE_TYPE) {
          this.setState({ contactMobile: contactData });
        } else if (contact.contactType === EMAIL_TYPE) {
          this.setState({ contactMail: contactData });
        }
      }
      this.setState({ isFetching: false });
    } catch (err) {
      goToErrorPage();
    }
  };

  /**
   * Get full contact data
   * @param {Object} contact 
   */
  getContactData = async (contact) => {
    const { identitiesId } = this.props;

    try {
      const res = await ApiService.getContactWayData({
        identitiesId,
        hashId: contact.contactId,
      });
      return res.data.data;
    } catch (err) {
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { contactMobile, contactMail } = this.state;

    // Set helper when contacts changes
    if (
      prevState.contactMobile !== contactMobile ||
      prevState.contactMail !== contactMail
    ) {
      this.setHelpMessage();
    }
  }

  /**
   * Set correct help message when empty contact
   */
  setHelpMessage = () => {
    const { contactMobile, contactMail } = this.state;

    if (!contactMobile && !contactMail) {
      this.setState({
        messageHelp: WordingService.getWording('hint', 'noContactAdvice'),
      });
    } else if (!contactMobile || !contactMail) {
      this.setState({
        messageHelp: WordingService.getWording('hint', 'addContactAdvice'),
      });
    } else {
      this.setState({
        messageHelp: null,
      });
    }
  }

  /**
   * Set value or error state on change
   */
  handleChange = (value, type) => {
    this.setState((state) => {
      return {
        contactMail: type === EMAIL_TYPE ? value.trim() : state.contactMail,
        contactMobile: type === MOBILE_TYPE ? value.trim() : state.contactMobile,
        errorMail: type === EMAIL_TYPE ? null : state.errorMail,
        errorMobile: type === MOBILE_TYPE ? null : state.errorMobile,
      };
    });
  };

  /**
   * Check credential error on blur
   */
  handleBlur = async (e, type) => {
    e.preventDefault();
    const { contactMobile, contactMail } = this.state;
    const contact = type === EMAIL_TYPE ? contactMail : contactMobile;

    if (contact) {
      try {
        await ApiService.checkCredentials({ login: contact });
      } catch (err) {
        if (err.response && err.response.data && err.response.data.code) {
          switch (err.response.data.code) {
            case 412004:
              this.setState((state) => {
                return {
                  errorMobile: type === MOBILE_TYPE ? WordingService.getWording('errorMessage', 'mobileIsInvalid') : state.errorMobile,
                  errorMail: type === EMAIL_TYPE ? WordingService.getWording('errorMessage', 'mailIsInvalid') : state.errorMail,
                };
              });
              break;
            default:
              break;
          }
        }
      }
    }
  };

  /**
   * Handle submit form
   */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { goToErrorPage, goToNextStep } = this.props;
    const {
      contactMobile, contactMail,
    } = this.state;

    TagService.sendAction('updateContacts');

    try {
      const res = await ApiService.updateContacts({
        mobile: contactMobile,
        mail: contactMail,
      });
      goToNextStep(res.data.step)
    } catch (err) {
      if (err.response && err.response.data && err.response.data.code) {
        switch (err.response.data.code) {
          case 412004:
            if (err.response.data.field === "mobile") {
              this.setState({ errorMobile: WordingService.getWording('errorMessage', 'mobileIsInvalid') });
            } else if (err.response.data.field === "mail") {
              this.setState({ errorMail: WordingService.getWording('errorMessage', 'mailIsInvalid') });
            }
            break;
          case 412005:
            this.setState({ errorMail: WordingService.getWording('errorMessage', 'mailIsUnknown') });
            break;
          default:
            goToErrorPage();
            break;
        }
      }
    }
  };

  /**
   * Get if one contact have error
   */
  isContactError = () => {
    const { errorMail, errorMobile } = this.state;

    return errorMail !== null || errorMobile !== null;
  };

  render() {
    const {
      contactMobile, contactMail, errorMobile, errorMail, messageHelp, isFetching, buttonLabel,
    } = this.state;

    if (isFetching) {
      return <Spinner />;
    }
    return (
      <form onSubmit={this.handleSubmit}>
        <Row>
          <Col xs={12} sm={12} md={9} lg={6} xl={6}>
            <Text id="add-contact-message">
              {WordingService.getWording('caption', 'addNewContacts')}
            </Text>
            <br />
            <TextInput
              id="add-mobile"
              value={contactMobile || ''}
              errorMessage={errorMobile}
              errorWithIcon={true}
              valid={!errorMobile}
              maxLength={15}
              onBlur={e => this.handleBlur(e, MOBILE_TYPE)}
              onChange={value => this.handleChange(value, MOBILE_TYPE)}
              label={WordingService.getWording('caption', 'addMobile')}
            />
            <br />
            <TextInput
              id="add-mail"
              value={contactMail || ''}
              errorMessage={errorMail}
              errorWithIcon={true}
              valid={!errorMail}
              maxLength={254}
              onBlur={e => this.handleBlur(e, EMAIL_TYPE)}
              onChange={value => this.handleChange(value, EMAIL_TYPE)}
              label={WordingService.getWording('caption', 'addMail')}
            />
            <br />
            <Helper id="no-contact-warning" display={messageHelp}>
              <Text>
                {messageHelp}
              </Text>
            </Helper>
            <Button htmlType="submit" id="submit-contacts" type="fill" disabled={this.isContactError()}>
              {buttonLabel}
            </Button>
          </Col>
        </Row>
      </form>
    );
  }
}

ContactView.defaultProps = {
  contacts: [],
};

ContactView.propTypes = {
  /** Function to get contacts */
  getFullContacts: PropTypes.func.isRequired,
  /** Array of contacts */
  contacts: PropTypes.array,
  goToNextStep: PropTypes.func.isRequired,
  goToErrorPage: PropTypes.func.isRequired,
  identitiesId: PropTypes.string.isRequired,
};

export default ContactView;
